import { useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Form, Input, InputNumber, Select, Space, Switch, Tag, Typography } from "antd";
import type { FactoryIntegration } from "@/core/models/factory-integration-settings.model";
import { factoryIntegrationSettingsRepo, FactorySettingsError } from "@/core/repos/factory-integration-settings.repo";
import { initialIntegrationValues, integrationUpdate } from "./settings-form.helpers";

interface Props { integration: FactoryIntegration; writable: boolean; guard?:()=>()=>boolean; onSaved: (item: FactoryIntegration) => void; }
const supportedTypes = new Set(["string", "secret", "boolean", "integer", "select", "url"]);

export default function IntegrationSettingsForm({ integration, writable, guard, onSaved }: Props) {
  const [form] = Form.useForm();
  const [working, setWorking] = useState<"validate" | "save" | null>(null);
  const [clearSecrets, setClearSecrets] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [needsReload, setNeedsReload] = useState(false);
  const active = useRef(true);
  const inFlight = useRef(false);
  const enabled = Form.useWatch("enabled", form);
  const unsupported = integration.fields.some(field => !supportedTypes.has(field.type));
  const disabled = !writable || !!working || needsReload || unsupported;
  useEffect(() => { active.current = true; return () => { active.current = false; form.resetFields(); }; }, [form]);

  async function submit(action: "validate" | "save") {
    const permitted=guard?.()||(()=>writable);
    if (disabled || inFlight.current||!permitted()) return;
    inFlight.current = true;
    let values: Record<string, unknown>;
    try { values = await form.validateFields(); } catch { inFlight.current = false; return; }
    if (!active.current||!permitted()) {inFlight.current=false;return;}
    setWorking(action); setError(""); setResult("");
    try {
      const input = integrationUpdate(integration, values, clearSecrets);
      if (action === "validate") {
        const validation = await factoryIntegrationSettingsRepo.validate(integration.id, input);
        if (active.current&&permitted()) setResult(validation.success ? "Cấu hình hợp lệ. Chưa xác thực tài khoản hoặc thực hiện giao dịch với nhà cung cấp." : "Cấu hình chưa hợp lệ.");
      } else {
        const saved = await factoryIntegrationSettingsRepo.save(integration.id, input);
        if (active.current && permitted() && saved.id === integration.id) {
          form.resetFields();
          setClearSecrets([]);
          setResult("Đã lưu cấu hình.");
          onSaved(saved);
        }
      }
    } catch (failure) {
      if (active.current) {
        setError(failure instanceof FactorySettingsError ? failure.message : "Không cập nhật được cấu hình. Tải lại cấu hình để kiểm tra kết quả.");
        if (!(failure instanceof FactorySettingsError) || failure.status === 409 || failure.status === 0) setNeedsReload(true);
      }
    } finally { if (active.current) { setWorking(null); inFlight.current = false; } }
  }

  return <section style={{ maxWidth: 850 }}>
    <Space align="center" wrap className="mb-2"><Typography.Title level={5} style={{ margin: 0 }}>{integration.title}</Typography.Title>
      <Tag>{["web", "override", "database"].includes(integration.source) ? "Đã cấu hình trên web" : "Cấu hình hiện có"}</Tag>
    </Space>
    {integration.description && <Typography.Paragraph type="secondary">{integration.description}</Typography.Paragraph>}
    {error && <Alert className="mb-3" type="error" showIcon message={error} />}
    {result && <Alert className="mb-3" type="success" showIcon message={result} />}
    {unsupported && <Alert className="mb-3" type="warning" showIcon message="Giao diện chưa hỗ trợ một trường cấu hình từ máy chủ. Vui lòng cập nhật web trước khi lưu." />}
    <Form form={form} name={`integration-${integration.id}`} layout="vertical" initialValues={initialIntegrationValues(integration)} autoComplete="off" disabled={disabled}
      onValuesChange={() => { setError(""); setResult(""); }}>
      {integration.fields.filter(field => supportedTypes.has(field.type)).map(field => {
        const hasSecret = field.type === "secret" && !!integration.secretStates[field.key];
        const required = field.required && enabled !== false && !(field.type === "secret" && hasSecret);
        const rules = field.type === "boolean" ? [] : [{ required, message: `Nhập ${field.label.toLowerCase()}.` },
          ...(field.type === "url" ? [{ validator: (_: unknown, value: unknown) => {
            if (!value) return Promise.resolve();
            try { const url = new URL(String(value)); if (["https:", "http:"].includes(url.protocol)) return Promise.resolve(); } catch { /* handled below */ }
            return Promise.reject(new Error("Nhập địa chỉ HTTP hoặc HTTPS hợp lệ."));
          } }] : [])];
        let input;
        if (field.type === "secret") input = <Input.Password autoComplete="new-password" visibilityToggle={false} maxLength={4096} disabled={disabled || clearSecrets.includes(field.key)} />;
        else if (field.type === "boolean") input = <Switch />;
        else if (field.type === "integer") input = <InputNumber precision={0} min={field.min ?? undefined} max={field.max ?? undefined} style={{ width: "100%" }} />;
        else if (field.type === "select") input = <Select options={(field.options || []).map(value => ({ value,
          label: integration.id === "invoice" && field.key === "provider" && value.toLowerCase() === "easyinvoice" ? "EasyInvoice — chưa hỗ trợ phát hành" : value,
          disabled: integration.id === "invoice" && field.key === "provider" && value.toLowerCase() === "easyinvoice",
        }))} />;
        else input = <Input autoComplete="off" maxLength={4096} inputMode={field.type === "url" ? "url" : undefined} />;
        return <div key={field.key}>
          <Form.Item name={field.key} label={field.label} valuePropName={field.type === "boolean" ? "checked" : "value"} rules={rules}
            extra={field.type === "secret" ? hasSecret ? "Đã có thông tin xác thực. Để trống để giữ nguyên; đổi tài khoản cần nhập thông tin xác thực mới." : "Chưa có thông tin xác thực riêng cho nhà máy này." : undefined}>
            {input}
          </Form.Item>
          {hasSecret && <Checkbox className="mb-4" disabled={disabled} checked={clearSecrets.includes(field.key)} onChange={event => {
            setClearSecrets(previous => event.target.checked ? [...previous, field.key] : previous.filter(key => key !== field.key));
            if (event.target.checked) form.setFieldValue(field.key, "");
          }}>Xóa {field.label.toLowerCase()} đã lưu</Checkbox>}
        </div>;
      })}
    </Form>
    <Space wrap>
      <Button onClick={() => void submit("validate")} loading={working === "validate"} disabled={disabled}>Kiểm tra cấu hình</Button>
      <Button type="primary" onClick={() => void submit("save")} loading={working === "save"} disabled={disabled}>Lưu cấu hình</Button>
    </Space>
    <Typography.Paragraph type="secondary" className="mt-3" style={{ marginBottom: 0 }}>Kiểm tra cấu hình chỉ kiểm tra định dạng và các trường bắt buộc; không chuyển tiền, phát hành hóa đơn hoặc ghi sổ kế toán.</Typography.Paragraph>
  </section>;
}
